import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ErrorObject, FieldProps, IAnswerState } from '../domain/usecases';
import { useSurveyForm } from '../hooks/useSurveyForm';

function usePrevious(value: any) {
  const ref = useRef(value);
  return ref;
}

function isFilled(answer: IAnswerState | undefined): boolean {
  const values = answer?.values;
  if (values === undefined || values === null) return false;
  if (Array.isArray(values)) return values.length > 0;
  return values !== '';
}

function matchesOption(
  answer: IAnswerState | undefined,
  expected: string,
): boolean {
  const values = answer?.values;
  if (Array.isArray(values)) {
    return values.map(String).includes(String(expected));
  }
  return String(values) === String(expected);
}

export function Field<T>({
  question,
  render: Render,
  onSubmit,
}: FieldProps<T>): JSX.Element | null {
  const {
    getValues,
    subscribe,
    registerField,
    onChange,
    unregisterField,
    errors,
    viewOnly,
    onChangeTitleField,
  } = useSurveyForm();

  const { id, required, parentQuestionId, parentOptionId } = question;

  const parentIds = useMemo<string[]>(() => {
    if (!parentQuestionId) return [];
    return Array.isArray(parentQuestionId)
      ? parentQuestionId
      : [parentQuestionId];
  }, [parentQuestionId]);

  const parentOptionMap = useMemo<Record<string, string>>(() => {
    if (!parentOptionId) return {};
    if (typeof parentOptionId === 'string') {
      return parentIds.length === 1 ? { [parentIds[0]]: parentOptionId } : {};
    }
    return parentOptionId;
  }, [parentOptionId, parentIds]);

  const previousValues = getValues(question.id);
  let initialValues = previousValues;
  if (initialValues === undefined && question.initValue) {
    initialValues = { answeredAt: new Date(), values: question.initValue };
  }
  const [visible, setVisible] = useState<boolean>(parentIds.length === 0);
  const [error, setError] = useState<ErrorObject | undefined>(undefined);
  const prevVisible = usePrevious(parentIds.length === 0);

  useEffect(() => {
    if (errors[question.id]) {
      setError(errors[question.id]);
    }
  }, [errors, question.id]);

  const computeVisible = useCallback(() => {
    return parentIds.every((pid) => {
      const answer = getValues(pid);
      if (!isFilled(answer)) return false;
      if (pid in parentOptionMap)
        return matchesOption(answer, parentOptionMap[pid]);
      return true;
    });
  }, [parentIds, parentOptionMap, getValues]);

  const callback = useCallback(() => {
    const isVisible = computeVisible();

    if (isVisible && !prevVisible.current) {
      prevVisible.current = isVisible;
      setVisible(isVisible);
      registerField({ id, required });
      return;
    }
    if (!isVisible && prevVisible.current) {
      prevVisible.current = isVisible;
      setVisible(false);
      unregisterField(id);
    }
  }, [
    computeVisible,
    id,
    prevVisible,
    required,
    registerField,
    unregisterField,
  ]);

  const onChangeCallback = useCallback(
    (value: IAnswerState) => {
      setError(undefined);
      onChange(id, value);
    },
    [id, onChange],
  );

  const onChangeTitleFieldCallback = useCallback(
    (value: IAnswerState) => {
      setError(undefined);
      onChangeTitleField(value);
    },
    [onChangeTitleField],
  );

  useEffect(() => {
    if (!parentIds.length) return undefined;
    const unsubs = parentIds.map((pid) => subscribe(pid, callback));
    return () => {
      unsubs.forEach((unsub) => unsub && unsub());
    };
  }, [parentIds, callback, subscribe]);

  useEffect(() => {
    if (!previousValues && initialValues && visible) {
      onChangeCallback(initialValues);
    }
  }, [onChangeCallback, previousValues, initialValues, visible]);

  if (!visible) return null;

  return (
    <>
      <Render
        initialValues={initialValues}
        onChange={onChangeCallback}
        onChangeScheduleTitle={onChangeTitleFieldCallback}
        question={question}
        onSubmit={onSubmit}
        viewOnly={viewOnly}
        errors={error}
      />
    </>
  );
}
